import { type Address, BITS, type Family } from "./parse";
import { load, type Reading, shardFor, shardStart, useShard } from "./shards";
import index from "./tables/roa-index.json";
import { writeValue } from "./write";

// The Route Origin Authorisations the five RIRs publish: a prefix holder's signed statement that a given AS may
// originate a given prefix. It is a registration and not an observation, which is the whole reason it is here — a
// routing-table snapshot says who was announcing something, and this says who is allowed to. What is signed is
// published so anybody may validate it, so unlike the whois database there is nothing to ask permission for.

const INDEX = index as { release: string; v4: string[]; v6: string[] };

// Every authorisation covering an address, widest first — a shard is sorted by address and then by width, so that is
// the order they come out in and the card turns it round. Nothing found is a real answer and not a gap: an address in
// no ROA is the unknown state routing security is looking for.
export interface Origins {
  covering: Roa[];
  release: string;
}

export interface Roa {
  cidr: string;
  prefix: number;
  // How far below its own width the authorisation reaches, which is what decides whether a longer announcement out of
  // the same AS would be valid or an overclaim.
  maxLength: number;
  origins: number[];
}

// Nothing at all is a shard that would not load, which is not the same answer as a shard holding no authorisation:
// the first is a claim about the network and the second is a claim about the registries.
export function originsOf({ family, value }: Address): Promise<Origins | undefined> {
  return family === "ipv4" ? readV4(value) : readV6(value);
}

export function useOrigins(address: Address | undefined): Reading<Origins> {
  return useShard(address && `${address.family}:${address.value}`, (asked) => {
    const [family, value] = asked.split(":");
    return family === "ipv4" ? readV4(BigInt(value)) : readV6(BigInt(value));
  });
}

// The shard holding an address holds every authorisation covering it, the generator having repeated the few that
// straddle a boundary — a /12 signed once may sit hundreds of files earlier, and scanning back for it is the one
// thing a lookup must never do.
async function readV4(value: bigint): Promise<Origins | undefined> {
  const shard = shardFor(INDEX.v4, value);
  if (shard < 0) return { covering: [], release: INDEX.release };

  const rows = await load<[number, number, number, number[]][]>(url("v4", shard));
  if (!rows) return undefined;

  const covering: Roa[] = [];
  let start = shardStart(INDEX.v4, shard);
  for (const [step, prefix, maxLength, origins] of rows) {
    start += BigInt(step);
    if (holds(start, prefix, value, "ipv4")) covering.push(roa(start, prefix, maxLength, origins, "ipv4"));
  }
  return { covering, release: INDEX.release };
}

async function readV6(value: bigint): Promise<Origins | undefined> {
  const shard = shardFor(INDEX.v6, value);
  if (shard < 0) return { covering: [], release: INDEX.release };

  const rows = await load<[string, number, number, number[]][]>(url("v6", shard));
  if (!rows) return undefined;

  const covering: Roa[] = [];
  for (const [start, prefix, maxLength, origins] of rows) {
    const at = BigInt(`0x${start}`);
    if (holds(at, prefix, value, "ipv6")) covering.push(roa(at, prefix, maxLength, origins, "ipv6"));
  }
  return { covering, release: INDEX.release };
}

function holds(start: bigint, prefix: number, value: bigint, family: Family): boolean {
  return value >= start && value <= start + (1n << BigInt(BITS[family] - prefix)) - 1n;
}

function roa(start: bigint, prefix: number, maxLength: number, origins: number[], family: Family): Roa {
  return { cidr: `${writeValue(start, family)}/${prefix}`, prefix, maxLength, origins };
}

// `?url` is what keeps 25 MB out of this chunk: each shard is emitted as an asset of its own and read only when
// somebody asks about an address inside it.
const FILES = { "./roas/v4-0.json": __glob_0, "./roas/v4-1.json": __glob_1, "./roas/v4-10.json": __glob_2, "./roas/v4-100.json": __glob_3, "./roas/v4-101.json": __glob_4, "./roas/v4-102.json": __glob_5, "./roas/v4-103.json": __glob_6, "./roas/v4-104.json": __glob_7, "./roas/v4-105.json": __glob_8, "./roas/v4-106.json": __glob_9, "./roas/v4-107.json": __glob_10, "./roas/v4-108.json": __glob_11, "./roas/v4-109.json": __glob_12, "./roas/v4-11.json": __glob_13, "./roas/v4-110.json": __glob_14, "./roas/v4-111.json": __glob_15, "./roas/v4-112.json": __glob_16, "./roas/v4-113.json": __glob_17, "./roas/v4-114.json": __glob_18, "./roas/v4-115.json": __glob_19, "./roas/v4-116.json": __glob_20, "./roas/v4-117.json": __glob_21, "./roas/v4-118.json": __glob_22, "./roas/v4-119.json": __glob_23, "./roas/v4-12.json": __glob_24, "./roas/v4-120.json": __glob_25, "./roas/v4-121.json": __glob_26, "./roas/v4-122.json": __glob_27, "./roas/v4-123.json": __glob_28, "./roas/v4-124.json": __glob_29, "./roas/v4-125.json": __glob_30, "./roas/v4-126.json": __glob_31, "./roas/v4-127.json": __glob_32, "./roas/v4-128.json": __glob_33, "./roas/v4-129.json": __glob_34, "./roas/v4-13.json": __glob_35, "./roas/v4-130.json": __glob_36, "./roas/v4-131.json": __glob_37, "./roas/v4-132.json": __glob_38, "./roas/v4-133.json": __glob_39, "./roas/v4-134.json": __glob_40, "./roas/v4-135.json": __glob_41, "./roas/v4-136.json": __glob_42, "./roas/v4-137.json": __glob_43, "./roas/v4-138.json": __glob_44, "./roas/v4-139.json": __glob_45, "./roas/v4-14.json": __glob_46, "./roas/v4-140.json": __glob_47, "./roas/v4-141.json": __glob_48, "./roas/v4-142.json": __glob_49, "./roas/v4-143.json": __glob_50, "./roas/v4-144.json": __glob_51, "./roas/v4-145.json": __glob_52, "./roas/v4-146.json": __glob_53, "./roas/v4-147.json": __glob_54, "./roas/v4-148.json": __glob_55, "./roas/v4-149.json": __glob_56, "./roas/v4-15.json": __glob_57, "./roas/v4-150.json": __glob_58, "./roas/v4-151.json": __glob_59, "./roas/v4-152.json": __glob_60, "./roas/v4-153.json": __glob_61, "./roas/v4-154.json": __glob_62, "./roas/v4-155.json": __glob_63, "./roas/v4-156.json": __glob_64, "./roas/v4-157.json": __glob_65, "./roas/v4-158.json": __glob_66, "./roas/v4-159.json": __glob_67, "./roas/v4-16.json": __glob_68, "./roas/v4-160.json": __glob_69, "./roas/v4-161.json": __glob_70, "./roas/v4-162.json": __glob_71, "./roas/v4-163.json": __glob_72, "./roas/v4-164.json": __glob_73, "./roas/v4-165.json": __glob_74, "./roas/v4-166.json": __glob_75, "./roas/v4-167.json": __glob_76, "./roas/v4-168.json": __glob_77, "./roas/v4-169.json": __glob_78, "./roas/v4-17.json": __glob_79, "./roas/v4-170.json": __glob_80, "./roas/v4-171.json": __glob_81, "./roas/v4-172.json": __glob_82, "./roas/v4-173.json": __glob_83, "./roas/v4-174.json": __glob_84, "./roas/v4-175.json": __glob_85, "./roas/v4-176.json": __glob_86, "./roas/v4-177.json": __glob_87, "./roas/v4-178.json": __glob_88, "./roas/v4-179.json": __glob_89, "./roas/v4-18.json": __glob_90, "./roas/v4-180.json": __glob_91, "./roas/v4-181.json": __glob_92, "./roas/v4-182.json": __glob_93, "./roas/v4-183.json": __glob_94, "./roas/v4-184.json": __glob_95, "./roas/v4-185.json": __glob_96, "./roas/v4-186.json": __glob_97, "./roas/v4-187.json": __glob_98, "./roas/v4-188.json": __glob_99, "./roas/v4-189.json": __glob_100, "./roas/v4-19.json": __glob_101, "./roas/v4-190.json": __glob_102, "./roas/v4-191.json": __glob_103, "./roas/v4-192.json": __glob_104, "./roas/v4-193.json": __glob_105, "./roas/v4-194.json": __glob_106, "./roas/v4-195.json": __glob_107, "./roas/v4-196.json": __glob_108, "./roas/v4-197.json": __glob_109, "./roas/v4-198.json": __glob_110, "./roas/v4-199.json": __glob_111, "./roas/v4-2.json": __glob_112, "./roas/v4-20.json": __glob_113, "./roas/v4-200.json": __glob_114, "./roas/v4-201.json": __glob_115, "./roas/v4-202.json": __glob_116, "./roas/v4-203.json": __glob_117, "./roas/v4-204.json": __glob_118, "./roas/v4-205.json": __glob_119, "./roas/v4-206.json": __glob_120, "./roas/v4-207.json": __glob_121, "./roas/v4-208.json": __glob_122, "./roas/v4-209.json": __glob_123, "./roas/v4-21.json": __glob_124, "./roas/v4-210.json": __glob_125, "./roas/v4-211.json": __glob_126, "./roas/v4-212.json": __glob_127, "./roas/v4-213.json": __glob_128, "./roas/v4-214.json": __glob_129, "./roas/v4-215.json": __glob_130, "./roas/v4-216.json": __glob_131, "./roas/v4-217.json": __glob_132, "./roas/v4-218.json": __glob_133, "./roas/v4-219.json": __glob_134, "./roas/v4-22.json": __glob_135, "./roas/v4-220.json": __glob_136, "./roas/v4-221.json": __glob_137, "./roas/v4-222.json": __glob_138, "./roas/v4-223.json": __glob_139, "./roas/v4-224.json": __glob_140, "./roas/v4-225.json": __glob_141, "./roas/v4-226.json": __glob_142, "./roas/v4-227.json": __glob_143, "./roas/v4-228.json": __glob_144, "./roas/v4-229.json": __glob_145, "./roas/v4-23.json": __glob_146, "./roas/v4-230.json": __glob_147, "./roas/v4-231.json": __glob_148, "./roas/v4-232.json": __glob_149, "./roas/v4-233.json": __glob_150, "./roas/v4-234.json": __glob_151, "./roas/v4-235.json": __glob_152, "./roas/v4-236.json": __glob_153, "./roas/v4-237.json": __glob_154, "./roas/v4-238.json": __glob_155, "./roas/v4-239.json": __glob_156, "./roas/v4-24.json": __glob_157, "./roas/v4-240.json": __glob_158, "./roas/v4-241.json": __glob_159, "./roas/v4-242.json": __glob_160, "./roas/v4-243.json": __glob_161, "./roas/v4-244.json": __glob_162, "./roas/v4-245.json": __glob_163, "./roas/v4-246.json": __glob_164, "./roas/v4-247.json": __glob_165, "./roas/v4-248.json": __glob_166, "./roas/v4-249.json": __glob_167, "./roas/v4-25.json": __glob_168, "./roas/v4-250.json": __glob_169, "./roas/v4-251.json": __glob_170, "./roas/v4-252.json": __glob_171, "./roas/v4-253.json": __glob_172, "./roas/v4-254.json": __glob_173, "./roas/v4-255.json": __glob_174, "./roas/v4-256.json": __glob_175, "./roas/v4-257.json": __glob_176, "./roas/v4-258.json": __glob_177, "./roas/v4-259.json": __glob_178, "./roas/v4-26.json": __glob_179, "./roas/v4-260.json": __glob_180, "./roas/v4-261.json": __glob_181, "./roas/v4-262.json": __glob_182, "./roas/v4-263.json": __glob_183, "./roas/v4-264.json": __glob_184, "./roas/v4-265.json": __glob_185, "./roas/v4-266.json": __glob_186, "./roas/v4-267.json": __glob_187, "./roas/v4-268.json": __glob_188, "./roas/v4-269.json": __glob_189, "./roas/v4-27.json": __glob_190, "./roas/v4-270.json": __glob_191, "./roas/v4-271.json": __glob_192, "./roas/v4-272.json": __glob_193, "./roas/v4-273.json": __glob_194, "./roas/v4-274.json": __glob_195, "./roas/v4-275.json": __glob_196, "./roas/v4-276.json": __glob_197, "./roas/v4-277.json": __glob_198, "./roas/v4-278.json": __glob_199, "./roas/v4-279.json": __glob_200, "./roas/v4-28.json": __glob_201, "./roas/v4-280.json": __glob_202, "./roas/v4-281.json": __glob_203, "./roas/v4-282.json": __glob_204, "./roas/v4-283.json": __glob_205, "./roas/v4-284.json": __glob_206, "./roas/v4-285.json": __glob_207, "./roas/v4-286.json": __glob_208, "./roas/v4-287.json": __glob_209, "./roas/v4-288.json": __glob_210, "./roas/v4-289.json": __glob_211, "./roas/v4-29.json": __glob_212, "./roas/v4-290.json": __glob_213, "./roas/v4-291.json": __glob_214, "./roas/v4-292.json": __glob_215, "./roas/v4-293.json": __glob_216, "./roas/v4-294.json": __glob_217, "./roas/v4-295.json": __glob_218, "./roas/v4-296.json": __glob_219, "./roas/v4-297.json": __glob_220, "./roas/v4-298.json": __glob_221, "./roas/v4-299.json": __glob_222, "./roas/v4-3.json": __glob_223, "./roas/v4-30.json": __glob_224, "./roas/v4-300.json": __glob_225, "./roas/v4-301.json": __glob_226, "./roas/v4-302.json": __glob_227, "./roas/v4-303.json": __glob_228, "./roas/v4-304.json": __glob_229, "./roas/v4-305.json": __glob_230, "./roas/v4-306.json": __glob_231, "./roas/v4-307.json": __glob_232, "./roas/v4-308.json": __glob_233, "./roas/v4-309.json": __glob_234, "./roas/v4-31.json": __glob_235, "./roas/v4-310.json": __glob_236, "./roas/v4-311.json": __glob_237, "./roas/v4-312.json": __glob_238, "./roas/v4-313.json": __glob_239, "./roas/v4-314.json": __glob_240, "./roas/v4-315.json": __glob_241, "./roas/v4-316.json": __glob_242, "./roas/v4-317.json": __glob_243, "./roas/v4-318.json": __glob_244, "./roas/v4-319.json": __glob_245, "./roas/v4-32.json": __glob_246, "./roas/v4-320.json": __glob_247, "./roas/v4-321.json": __glob_248, "./roas/v4-322.json": __glob_249, "./roas/v4-323.json": __glob_250, "./roas/v4-324.json": __glob_251, "./roas/v4-325.json": __glob_252, "./roas/v4-326.json": __glob_253, "./roas/v4-327.json": __glob_254, "./roas/v4-328.json": __glob_255, "./roas/v4-329.json": __glob_256, "./roas/v4-33.json": __glob_257, "./roas/v4-330.json": __glob_258, "./roas/v4-34.json": __glob_259, "./roas/v4-35.json": __glob_260, "./roas/v4-36.json": __glob_261, "./roas/v4-37.json": __glob_262, "./roas/v4-38.json": __glob_263, "./roas/v4-39.json": __glob_264, "./roas/v4-4.json": __glob_265, "./roas/v4-40.json": __glob_266, "./roas/v4-41.json": __glob_267, "./roas/v4-42.json": __glob_268, "./roas/v4-43.json": __glob_269, "./roas/v4-44.json": __glob_270, "./roas/v4-45.json": __glob_271, "./roas/v4-46.json": __glob_272, "./roas/v4-47.json": __glob_273, "./roas/v4-48.json": __glob_274, "./roas/v4-49.json": __glob_275, "./roas/v4-5.json": __glob_276, "./roas/v4-50.json": __glob_277, "./roas/v4-51.json": __glob_278, "./roas/v4-52.json": __glob_279, "./roas/v4-53.json": __glob_280, "./roas/v4-54.json": __glob_281, "./roas/v4-55.json": __glob_282, "./roas/v4-56.json": __glob_283, "./roas/v4-57.json": __glob_284, "./roas/v4-58.json": __glob_285, "./roas/v4-59.json": __glob_286, "./roas/v4-6.json": __glob_287, "./roas/v4-60.json": __glob_288, "./roas/v4-61.json": __glob_289, "./roas/v4-62.json": __glob_290, "./roas/v4-63.json": __glob_291, "./roas/v4-64.json": __glob_292, "./roas/v4-65.json": __glob_293, "./roas/v4-66.json": __glob_294, "./roas/v4-67.json": __glob_295, "./roas/v4-68.json": __glob_296, "./roas/v4-69.json": __glob_297, "./roas/v4-7.json": __glob_298, "./roas/v4-70.json": __glob_299, "./roas/v4-71.json": __glob_300, "./roas/v4-72.json": __glob_301, "./roas/v4-73.json": __glob_302, "./roas/v4-74.json": __glob_303, "./roas/v4-75.json": __glob_304, "./roas/v4-76.json": __glob_305, "./roas/v4-77.json": __glob_306, "./roas/v4-78.json": __glob_307, "./roas/v4-79.json": __glob_308, "./roas/v4-8.json": __glob_309, "./roas/v4-80.json": __glob_310, "./roas/v4-81.json": __glob_311, "./roas/v4-82.json": __glob_312, "./roas/v4-83.json": __glob_313, "./roas/v4-84.json": __glob_314, "./roas/v4-85.json": __glob_315, "./roas/v4-86.json": __glob_316, "./roas/v4-87.json": __glob_317, "./roas/v4-88.json": __glob_318, "./roas/v4-89.json": __glob_319, "./roas/v4-9.json": __glob_320, "./roas/v4-90.json": __glob_321, "./roas/v4-91.json": __glob_322, "./roas/v4-92.json": __glob_323, "./roas/v4-93.json": __glob_324, "./roas/v4-94.json": __glob_325, "./roas/v4-95.json": __glob_326, "./roas/v4-96.json": __glob_327, "./roas/v4-97.json": __glob_328, "./roas/v4-98.json": __glob_329, "./roas/v4-99.json": __glob_330, "./roas/v6-0.json": __glob_331, "./roas/v6-1.json": __glob_332, "./roas/v6-10.json": __glob_333, "./roas/v6-11.json": __glob_334, "./roas/v6-12.json": __glob_335, "./roas/v6-13.json": __glob_336, "./roas/v6-14.json": __glob_337, "./roas/v6-15.json": __glob_338, "./roas/v6-16.json": __glob_339, "./roas/v6-17.json": __glob_340, "./roas/v6-18.json": __glob_341, "./roas/v6-19.json": __glob_342, "./roas/v6-2.json": __glob_343, "./roas/v6-20.json": __glob_344, "./roas/v6-21.json": __glob_345, "./roas/v6-22.json": __glob_346, "./roas/v6-23.json": __glob_347, "./roas/v6-24.json": __glob_348, "./roas/v6-25.json": __glob_349, "./roas/v6-26.json": __glob_350, "./roas/v6-27.json": __glob_351, "./roas/v6-28.json": __glob_352, "./roas/v6-29.json": __glob_353, "./roas/v6-3.json": __glob_354, "./roas/v6-30.json": __glob_355, "./roas/v6-31.json": __glob_356, "./roas/v6-32.json": __glob_357, "./roas/v6-33.json": __glob_358, "./roas/v6-34.json": __glob_359, "./roas/v6-35.json": __glob_360, "./roas/v6-36.json": __glob_361, "./roas/v6-37.json": __glob_362, "./roas/v6-38.json": __glob_363, "./roas/v6-39.json": __glob_364, "./roas/v6-4.json": __glob_365, "./roas/v6-40.json": __glob_366, "./roas/v6-41.json": __glob_367, "./roas/v6-42.json": __glob_368, "./roas/v6-43.json": __glob_369, "./roas/v6-44.json": __glob_370, "./roas/v6-45.json": __glob_371, "./roas/v6-46.json": __glob_372, "./roas/v6-47.json": __glob_373, "./roas/v6-48.json": __glob_374, "./roas/v6-49.json": __glob_375, "./roas/v6-5.json": __glob_376, "./roas/v6-50.json": __glob_377, "./roas/v6-51.json": __glob_378, "./roas/v6-52.json": __glob_379, "./roas/v6-53.json": __glob_380, "./roas/v6-54.json": __glob_381, "./roas/v6-55.json": __glob_382, "./roas/v6-56.json": __glob_383, "./roas/v6-57.json": __glob_384, "./roas/v6-58.json": __glob_385, "./roas/v6-59.json": __glob_386, "./roas/v6-6.json": __glob_387, "./roas/v6-60.json": __glob_388, "./roas/v6-61.json": __glob_389, "./roas/v6-62.json": __glob_390, "./roas/v6-63.json": __glob_391, "./roas/v6-64.json": __glob_392, "./roas/v6-65.json": __glob_393, "./roas/v6-66.json": __glob_394, "./roas/v6-67.json": __glob_395, "./roas/v6-68.json": __glob_396, "./roas/v6-69.json": __glob_397, "./roas/v6-7.json": __glob_398, "./roas/v6-70.json": __glob_399, "./roas/v6-71.json": __glob_400, "./roas/v6-72.json": __glob_401, "./roas/v6-73.json": __glob_402, "./roas/v6-74.json": __glob_403, "./roas/v6-75.json": __glob_404, "./roas/v6-76.json": __glob_405, "./roas/v6-77.json": __glob_406, "./roas/v6-78.json": __glob_407, "./roas/v6-79.json": __glob_408, "./roas/v6-8.json": __glob_409, "./roas/v6-80.json": __glob_410, "./roas/v6-81.json": __glob_411, "./roas/v6-82.json": __glob_412, "./roas/v6-83.json": __glob_413, "./roas/v6-84.json": __glob_414, "./roas/v6-85.json": __glob_415, "./roas/v6-86.json": __glob_416, "./roas/v6-87.json": __glob_417, "./roas/v6-88.json": __glob_418, "./roas/v6-89.json": __glob_419, "./roas/v6-9.json": __glob_420, "./roas/v6-90.json": __glob_421, "./roas/v6-91.json": __glob_422, "./roas/v6-92.json": __glob_423, "./roas/v6-93.json": __glob_424, "./roas/v6-94.json": __glob_425, "./roas/v6-95.json": __glob_426, "./roas/v6-96.json": __glob_427, "./roas/v6-97.json": __glob_428, "./roas/v6-98.json": __glob_429 } as Record<
  string,
  string
>;

function url(name: string, shard: number): string | undefined {
  return FILES[`./roas/${name}-${shard}.json`];
}

import __glob_0 from "./roas/v4-0.json?url";
import __glob_1 from "./roas/v4-1.json?url";
import __glob_2 from "./roas/v4-10.json?url";
import __glob_3 from "./roas/v4-100.json?url";
import __glob_4 from "./roas/v4-101.json?url";
import __glob_5 from "./roas/v4-102.json?url";
import __glob_6 from "./roas/v4-103.json?url";
import __glob_7 from "./roas/v4-104.json?url";
import __glob_8 from "./roas/v4-105.json?url";
import __glob_9 from "./roas/v4-106.json?url";
import __glob_10 from "./roas/v4-107.json?url";
import __glob_11 from "./roas/v4-108.json?url";
import __glob_12 from "./roas/v4-109.json?url";
import __glob_13 from "./roas/v4-11.json?url";
import __glob_14 from "./roas/v4-110.json?url";
import __glob_15 from "./roas/v4-111.json?url";
import __glob_16 from "./roas/v4-112.json?url";
import __glob_17 from "./roas/v4-113.json?url";
import __glob_18 from "./roas/v4-114.json?url";
import __glob_19 from "./roas/v4-115.json?url";
import __glob_20 from "./roas/v4-116.json?url";
import __glob_21 from "./roas/v4-117.json?url";
import __glob_22 from "./roas/v4-118.json?url";
import __glob_23 from "./roas/v4-119.json?url";
import __glob_24 from "./roas/v4-12.json?url";
import __glob_25 from "./roas/v4-120.json?url";
import __glob_26 from "./roas/v4-121.json?url";
import __glob_27 from "./roas/v4-122.json?url";
import __glob_28 from "./roas/v4-123.json?url";
import __glob_29 from "./roas/v4-124.json?url";
import __glob_30 from "./roas/v4-125.json?url";
import __glob_31 from "./roas/v4-126.json?url";
import __glob_32 from "./roas/v4-127.json?url";
import __glob_33 from "./roas/v4-128.json?url";
import __glob_34 from "./roas/v4-129.json?url";
import __glob_35 from "./roas/v4-13.json?url";
import __glob_36 from "./roas/v4-130.json?url";
import __glob_37 from "./roas/v4-131.json?url";
import __glob_38 from "./roas/v4-132.json?url";
import __glob_39 from "./roas/v4-133.json?url";
import __glob_40 from "./roas/v4-134.json?url";
import __glob_41 from "./roas/v4-135.json?url";
import __glob_42 from "./roas/v4-136.json?url";
import __glob_43 from "./roas/v4-137.json?url";
import __glob_44 from "./roas/v4-138.json?url";
import __glob_45 from "./roas/v4-139.json?url";
import __glob_46 from "./roas/v4-14.json?url";
import __glob_47 from "./roas/v4-140.json?url";
import __glob_48 from "./roas/v4-141.json?url";
import __glob_49 from "./roas/v4-142.json?url";
import __glob_50 from "./roas/v4-143.json?url";
import __glob_51 from "./roas/v4-144.json?url";
import __glob_52 from "./roas/v4-145.json?url";
import __glob_53 from "./roas/v4-146.json?url";
import __glob_54 from "./roas/v4-147.json?url";
import __glob_55 from "./roas/v4-148.json?url";
import __glob_56 from "./roas/v4-149.json?url";
import __glob_57 from "./roas/v4-15.json?url";
import __glob_58 from "./roas/v4-150.json?url";
import __glob_59 from "./roas/v4-151.json?url";
import __glob_60 from "./roas/v4-152.json?url";
import __glob_61 from "./roas/v4-153.json?url";
import __glob_62 from "./roas/v4-154.json?url";
import __glob_63 from "./roas/v4-155.json?url";
import __glob_64 from "./roas/v4-156.json?url";
import __glob_65 from "./roas/v4-157.json?url";
import __glob_66 from "./roas/v4-158.json?url";
import __glob_67 from "./roas/v4-159.json?url";
import __glob_68 from "./roas/v4-16.json?url";
import __glob_69 from "./roas/v4-160.json?url";
import __glob_70 from "./roas/v4-161.json?url";
import __glob_71 from "./roas/v4-162.json?url";
import __glob_72 from "./roas/v4-163.json?url";
import __glob_73 from "./roas/v4-164.json?url";
import __glob_74 from "./roas/v4-165.json?url";
import __glob_75 from "./roas/v4-166.json?url";
import __glob_76 from "./roas/v4-167.json?url";
import __glob_77 from "./roas/v4-168.json?url";
import __glob_78 from "./roas/v4-169.json?url";
import __glob_79 from "./roas/v4-17.json?url";
import __glob_80 from "./roas/v4-170.json?url";
import __glob_81 from "./roas/v4-171.json?url";
import __glob_82 from "./roas/v4-172.json?url";
import __glob_83 from "./roas/v4-173.json?url";
import __glob_84 from "./roas/v4-174.json?url";
import __glob_85 from "./roas/v4-175.json?url";
import __glob_86 from "./roas/v4-176.json?url";
import __glob_87 from "./roas/v4-177.json?url";
import __glob_88 from "./roas/v4-178.json?url";
import __glob_89 from "./roas/v4-179.json?url";
import __glob_90 from "./roas/v4-18.json?url";
import __glob_91 from "./roas/v4-180.json?url";
import __glob_92 from "./roas/v4-181.json?url";
import __glob_93 from "./roas/v4-182.json?url";
import __glob_94 from "./roas/v4-183.json?url";
import __glob_95 from "./roas/v4-184.json?url";
import __glob_96 from "./roas/v4-185.json?url";
import __glob_97 from "./roas/v4-186.json?url";
import __glob_98 from "./roas/v4-187.json?url";
import __glob_99 from "./roas/v4-188.json?url";
import __glob_100 from "./roas/v4-189.json?url";
import __glob_101 from "./roas/v4-19.json?url";
import __glob_102 from "./roas/v4-190.json?url";
import __glob_103 from "./roas/v4-191.json?url";
import __glob_104 from "./roas/v4-192.json?url";
import __glob_105 from "./roas/v4-193.json?url";
import __glob_106 from "./roas/v4-194.json?url";
import __glob_107 from "./roas/v4-195.json?url";
import __glob_108 from "./roas/v4-196.json?url";
import __glob_109 from "./roas/v4-197.json?url";
import __glob_110 from "./roas/v4-198.json?url";
import __glob_111 from "./roas/v4-199.json?url";
import __glob_112 from "./roas/v4-2.json?url";
import __glob_113 from "./roas/v4-20.json?url";
import __glob_114 from "./roas/v4-200.json?url";
import __glob_115 from "./roas/v4-201.json?url";
import __glob_116 from "./roas/v4-202.json?url";
import __glob_117 from "./roas/v4-203.json?url";
import __glob_118 from "./roas/v4-204.json?url";
import __glob_119 from "./roas/v4-205.json?url";
import __glob_120 from "./roas/v4-206.json?url";
import __glob_121 from "./roas/v4-207.json?url";
import __glob_122 from "./roas/v4-208.json?url";
import __glob_123 from "./roas/v4-209.json?url";
import __glob_124 from "./roas/v4-21.json?url";
import __glob_125 from "./roas/v4-210.json?url";
import __glob_126 from "./roas/v4-211.json?url";
import __glob_127 from "./roas/v4-212.json?url";
import __glob_128 from "./roas/v4-213.json?url";
import __glob_129 from "./roas/v4-214.json?url";
import __glob_130 from "./roas/v4-215.json?url";
import __glob_131 from "./roas/v4-216.json?url";
import __glob_132 from "./roas/v4-217.json?url";
import __glob_133 from "./roas/v4-218.json?url";
import __glob_134 from "./roas/v4-219.json?url";
import __glob_135 from "./roas/v4-22.json?url";
import __glob_136 from "./roas/v4-220.json?url";
import __glob_137 from "./roas/v4-221.json?url";
import __glob_138 from "./roas/v4-222.json?url";
import __glob_139 from "./roas/v4-223.json?url";
import __glob_140 from "./roas/v4-224.json?url";
import __glob_141 from "./roas/v4-225.json?url";
import __glob_142 from "./roas/v4-226.json?url";
import __glob_143 from "./roas/v4-227.json?url";
import __glob_144 from "./roas/v4-228.json?url";
import __glob_145 from "./roas/v4-229.json?url";
import __glob_146 from "./roas/v4-23.json?url";
import __glob_147 from "./roas/v4-230.json?url";
import __glob_148 from "./roas/v4-231.json?url";
import __glob_149 from "./roas/v4-232.json?url";
import __glob_150 from "./roas/v4-233.json?url";
import __glob_151 from "./roas/v4-234.json?url";
import __glob_152 from "./roas/v4-235.json?url";
import __glob_153 from "./roas/v4-236.json?url";
import __glob_154 from "./roas/v4-237.json?url";
import __glob_155 from "./roas/v4-238.json?url";
import __glob_156 from "./roas/v4-239.json?url";
import __glob_157 from "./roas/v4-24.json?url";
import __glob_158 from "./roas/v4-240.json?url";
import __glob_159 from "./roas/v4-241.json?url";
import __glob_160 from "./roas/v4-242.json?url";
import __glob_161 from "./roas/v4-243.json?url";
import __glob_162 from "./roas/v4-244.json?url";
import __glob_163 from "./roas/v4-245.json?url";
import __glob_164 from "./roas/v4-246.json?url";
import __glob_165 from "./roas/v4-247.json?url";
import __glob_166 from "./roas/v4-248.json?url";
import __glob_167 from "./roas/v4-249.json?url";
import __glob_168 from "./roas/v4-25.json?url";
import __glob_169 from "./roas/v4-250.json?url";
import __glob_170 from "./roas/v4-251.json?url";
import __glob_171 from "./roas/v4-252.json?url";
import __glob_172 from "./roas/v4-253.json?url";
import __glob_173 from "./roas/v4-254.json?url";
import __glob_174 from "./roas/v4-255.json?url";
import __glob_175 from "./roas/v4-256.json?url";
import __glob_176 from "./roas/v4-257.json?url";
import __glob_177 from "./roas/v4-258.json?url";
import __glob_178 from "./roas/v4-259.json?url";
import __glob_179 from "./roas/v4-26.json?url";
import __glob_180 from "./roas/v4-260.json?url";
import __glob_181 from "./roas/v4-261.json?url";
import __glob_182 from "./roas/v4-262.json?url";
import __glob_183 from "./roas/v4-263.json?url";
import __glob_184 from "./roas/v4-264.json?url";
import __glob_185 from "./roas/v4-265.json?url";
import __glob_186 from "./roas/v4-266.json?url";
import __glob_187 from "./roas/v4-267.json?url";
import __glob_188 from "./roas/v4-268.json?url";
import __glob_189 from "./roas/v4-269.json?url";
import __glob_190 from "./roas/v4-27.json?url";
import __glob_191 from "./roas/v4-270.json?url";
import __glob_192 from "./roas/v4-271.json?url";
import __glob_193 from "./roas/v4-272.json?url";
import __glob_194 from "./roas/v4-273.json?url";
import __glob_195 from "./roas/v4-274.json?url";
import __glob_196 from "./roas/v4-275.json?url";
import __glob_197 from "./roas/v4-276.json?url";
import __glob_198 from "./roas/v4-277.json?url";
import __glob_199 from "./roas/v4-278.json?url";
import __glob_200 from "./roas/v4-279.json?url";
import __glob_201 from "./roas/v4-28.json?url";
import __glob_202 from "./roas/v4-280.json?url";
import __glob_203 from "./roas/v4-281.json?url";
import __glob_204 from "./roas/v4-282.json?url";
import __glob_205 from "./roas/v4-283.json?url";
import __glob_206 from "./roas/v4-284.json?url";
import __glob_207 from "./roas/v4-285.json?url";
import __glob_208 from "./roas/v4-286.json?url";
import __glob_209 from "./roas/v4-287.json?url";
import __glob_210 from "./roas/v4-288.json?url";
import __glob_211 from "./roas/v4-289.json?url";
import __glob_212 from "./roas/v4-29.json?url";
import __glob_213 from "./roas/v4-290.json?url";
import __glob_214 from "./roas/v4-291.json?url";
import __glob_215 from "./roas/v4-292.json?url";
import __glob_216 from "./roas/v4-293.json?url";
import __glob_217 from "./roas/v4-294.json?url";
import __glob_218 from "./roas/v4-295.json?url";
import __glob_219 from "./roas/v4-296.json?url";
import __glob_220 from "./roas/v4-297.json?url";
import __glob_221 from "./roas/v4-298.json?url";
import __glob_222 from "./roas/v4-299.json?url";
import __glob_223 from "./roas/v4-3.json?url";
import __glob_224 from "./roas/v4-30.json?url";
import __glob_225 from "./roas/v4-300.json?url";
import __glob_226 from "./roas/v4-301.json?url";
import __glob_227 from "./roas/v4-302.json?url";
import __glob_228 from "./roas/v4-303.json?url";
import __glob_229 from "./roas/v4-304.json?url";
import __glob_230 from "./roas/v4-305.json?url";
import __glob_231 from "./roas/v4-306.json?url";
import __glob_232 from "./roas/v4-307.json?url";
import __glob_233 from "./roas/v4-308.json?url";
import __glob_234 from "./roas/v4-309.json?url";
import __glob_235 from "./roas/v4-31.json?url";
import __glob_236 from "./roas/v4-310.json?url";
import __glob_237 from "./roas/v4-311.json?url";
import __glob_238 from "./roas/v4-312.json?url";
import __glob_239 from "./roas/v4-313.json?url";
import __glob_240 from "./roas/v4-314.json?url";
import __glob_241 from "./roas/v4-315.json?url";
import __glob_242 from "./roas/v4-316.json?url";
import __glob_243 from "./roas/v4-317.json?url";
import __glob_244 from "./roas/v4-318.json?url";
import __glob_245 from "./roas/v4-319.json?url";
import __glob_246 from "./roas/v4-32.json?url";
import __glob_247 from "./roas/v4-320.json?url";
import __glob_248 from "./roas/v4-321.json?url";
import __glob_249 from "./roas/v4-322.json?url";
import __glob_250 from "./roas/v4-323.json?url";
import __glob_251 from "./roas/v4-324.json?url";
import __glob_252 from "./roas/v4-325.json?url";
import __glob_253 from "./roas/v4-326.json?url";
import __glob_254 from "./roas/v4-327.json?url";
import __glob_255 from "./roas/v4-328.json?url";
import __glob_256 from "./roas/v4-329.json?url";
import __glob_257 from "./roas/v4-33.json?url";
import __glob_258 from "./roas/v4-330.json?url";
import __glob_259 from "./roas/v4-34.json?url";
import __glob_260 from "./roas/v4-35.json?url";
import __glob_261 from "./roas/v4-36.json?url";
import __glob_262 from "./roas/v4-37.json?url";
import __glob_263 from "./roas/v4-38.json?url";
import __glob_264 from "./roas/v4-39.json?url";
import __glob_265 from "./roas/v4-4.json?url";
import __glob_266 from "./roas/v4-40.json?url";
import __glob_267 from "./roas/v4-41.json?url";
import __glob_268 from "./roas/v4-42.json?url";
import __glob_269 from "./roas/v4-43.json?url";
import __glob_270 from "./roas/v4-44.json?url";
import __glob_271 from "./roas/v4-45.json?url";
import __glob_272 from "./roas/v4-46.json?url";
import __glob_273 from "./roas/v4-47.json?url";
import __glob_274 from "./roas/v4-48.json?url";
import __glob_275 from "./roas/v4-49.json?url";
import __glob_276 from "./roas/v4-5.json?url";
import __glob_277 from "./roas/v4-50.json?url";
import __glob_278 from "./roas/v4-51.json?url";
import __glob_279 from "./roas/v4-52.json?url";
import __glob_280 from "./roas/v4-53.json?url";
import __glob_281 from "./roas/v4-54.json?url";
import __glob_282 from "./roas/v4-55.json?url";
import __glob_283 from "./roas/v4-56.json?url";
import __glob_284 from "./roas/v4-57.json?url";
import __glob_285 from "./roas/v4-58.json?url";
import __glob_286 from "./roas/v4-59.json?url";
import __glob_287 from "./roas/v4-6.json?url";
import __glob_288 from "./roas/v4-60.json?url";
import __glob_289 from "./roas/v4-61.json?url";
import __glob_290 from "./roas/v4-62.json?url";
import __glob_291 from "./roas/v4-63.json?url";
import __glob_292 from "./roas/v4-64.json?url";
import __glob_293 from "./roas/v4-65.json?url";
import __glob_294 from "./roas/v4-66.json?url";
import __glob_295 from "./roas/v4-67.json?url";
import __glob_296 from "./roas/v4-68.json?url";
import __glob_297 from "./roas/v4-69.json?url";
import __glob_298 from "./roas/v4-7.json?url";
import __glob_299 from "./roas/v4-70.json?url";
import __glob_300 from "./roas/v4-71.json?url";
import __glob_301 from "./roas/v4-72.json?url";
import __glob_302 from "./roas/v4-73.json?url";
import __glob_303 from "./roas/v4-74.json?url";
import __glob_304 from "./roas/v4-75.json?url";
import __glob_305 from "./roas/v4-76.json?url";
import __glob_306 from "./roas/v4-77.json?url";
import __glob_307 from "./roas/v4-78.json?url";
import __glob_308 from "./roas/v4-79.json?url";
import __glob_309 from "./roas/v4-8.json?url";
import __glob_310 from "./roas/v4-80.json?url";
import __glob_311 from "./roas/v4-81.json?url";
import __glob_312 from "./roas/v4-82.json?url";
import __glob_313 from "./roas/v4-83.json?url";
import __glob_314 from "./roas/v4-84.json?url";
import __glob_315 from "./roas/v4-85.json?url";
import __glob_316 from "./roas/v4-86.json?url";
import __glob_317 from "./roas/v4-87.json?url";
import __glob_318 from "./roas/v4-88.json?url";
import __glob_319 from "./roas/v4-89.json?url";
import __glob_320 from "./roas/v4-9.json?url";
import __glob_321 from "./roas/v4-90.json?url";
import __glob_322 from "./roas/v4-91.json?url";
import __glob_323 from "./roas/v4-92.json?url";
import __glob_324 from "./roas/v4-93.json?url";
import __glob_325 from "./roas/v4-94.json?url";
import __glob_326 from "./roas/v4-95.json?url";
import __glob_327 from "./roas/v4-96.json?url";
import __glob_328 from "./roas/v4-97.json?url";
import __glob_329 from "./roas/v4-98.json?url";
import __glob_330 from "./roas/v4-99.json?url";
import __glob_331 from "./roas/v6-0.json?url";
import __glob_332 from "./roas/v6-1.json?url";
import __glob_333 from "./roas/v6-10.json?url";
import __glob_334 from "./roas/v6-11.json?url";
import __glob_335 from "./roas/v6-12.json?url";
import __glob_336 from "./roas/v6-13.json?url";
import __glob_337 from "./roas/v6-14.json?url";
import __glob_338 from "./roas/v6-15.json?url";
import __glob_339 from "./roas/v6-16.json?url";
import __glob_340 from "./roas/v6-17.json?url";
import __glob_341 from "./roas/v6-18.json?url";
import __glob_342 from "./roas/v6-19.json?url";
import __glob_343 from "./roas/v6-2.json?url";
import __glob_344 from "./roas/v6-20.json?url";
import __glob_345 from "./roas/v6-21.json?url";
import __glob_346 from "./roas/v6-22.json?url";
import __glob_347 from "./roas/v6-23.json?url";
import __glob_348 from "./roas/v6-24.json?url";
import __glob_349 from "./roas/v6-25.json?url";
import __glob_350 from "./roas/v6-26.json?url";
import __glob_351 from "./roas/v6-27.json?url";
import __glob_352 from "./roas/v6-28.json?url";
import __glob_353 from "./roas/v6-29.json?url";
import __glob_354 from "./roas/v6-3.json?url";
import __glob_355 from "./roas/v6-30.json?url";
import __glob_356 from "./roas/v6-31.json?url";
import __glob_357 from "./roas/v6-32.json?url";
import __glob_358 from "./roas/v6-33.json?url";
import __glob_359 from "./roas/v6-34.json?url";
import __glob_360 from "./roas/v6-35.json?url";
import __glob_361 from "./roas/v6-36.json?url";
import __glob_362 from "./roas/v6-37.json?url";
import __glob_363 from "./roas/v6-38.json?url";
import __glob_364 from "./roas/v6-39.json?url";
import __glob_365 from "./roas/v6-4.json?url";
import __glob_366 from "./roas/v6-40.json?url";
import __glob_367 from "./roas/v6-41.json?url";
import __glob_368 from "./roas/v6-42.json?url";
import __glob_369 from "./roas/v6-43.json?url";
import __glob_370 from "./roas/v6-44.json?url";
import __glob_371 from "./roas/v6-45.json?url";
import __glob_372 from "./roas/v6-46.json?url";
import __glob_373 from "./roas/v6-47.json?url";
import __glob_374 from "./roas/v6-48.json?url";
import __glob_375 from "./roas/v6-49.json?url";
import __glob_376 from "./roas/v6-5.json?url";
import __glob_377 from "./roas/v6-50.json?url";
import __glob_378 from "./roas/v6-51.json?url";
import __glob_379 from "./roas/v6-52.json?url";
import __glob_380 from "./roas/v6-53.json?url";
import __glob_381 from "./roas/v6-54.json?url";
import __glob_382 from "./roas/v6-55.json?url";
import __glob_383 from "./roas/v6-56.json?url";
import __glob_384 from "./roas/v6-57.json?url";
import __glob_385 from "./roas/v6-58.json?url";
import __glob_386 from "./roas/v6-59.json?url";
import __glob_387 from "./roas/v6-6.json?url";
import __glob_388 from "./roas/v6-60.json?url";
import __glob_389 from "./roas/v6-61.json?url";
import __glob_390 from "./roas/v6-62.json?url";
import __glob_391 from "./roas/v6-63.json?url";
import __glob_392 from "./roas/v6-64.json?url";
import __glob_393 from "./roas/v6-65.json?url";
import __glob_394 from "./roas/v6-66.json?url";
import __glob_395 from "./roas/v6-67.json?url";
import __glob_396 from "./roas/v6-68.json?url";
import __glob_397 from "./roas/v6-69.json?url";
import __glob_398 from "./roas/v6-7.json?url";
import __glob_399 from "./roas/v6-70.json?url";
import __glob_400 from "./roas/v6-71.json?url";
import __glob_401 from "./roas/v6-72.json?url";
import __glob_402 from "./roas/v6-73.json?url";
import __glob_403 from "./roas/v6-74.json?url";
import __glob_404 from "./roas/v6-75.json?url";
import __glob_405 from "./roas/v6-76.json?url";
import __glob_406 from "./roas/v6-77.json?url";
import __glob_407 from "./roas/v6-78.json?url";
import __glob_408 from "./roas/v6-79.json?url";
import __glob_409 from "./roas/v6-8.json?url";
import __glob_410 from "./roas/v6-80.json?url";
import __glob_411 from "./roas/v6-81.json?url";
import __glob_412 from "./roas/v6-82.json?url";
import __glob_413 from "./roas/v6-83.json?url";
import __glob_414 from "./roas/v6-84.json?url";
import __glob_415 from "./roas/v6-85.json?url";
import __glob_416 from "./roas/v6-86.json?url";
import __glob_417 from "./roas/v6-87.json?url";
import __glob_418 from "./roas/v6-88.json?url";
import __glob_419 from "./roas/v6-89.json?url";
import __glob_420 from "./roas/v6-9.json?url";
import __glob_421 from "./roas/v6-90.json?url";
import __glob_422 from "./roas/v6-91.json?url";
import __glob_423 from "./roas/v6-92.json?url";
import __glob_424 from "./roas/v6-93.json?url";
import __glob_425 from "./roas/v6-94.json?url";
import __glob_426 from "./roas/v6-95.json?url";
import __glob_427 from "./roas/v6-96.json?url";
import __glob_428 from "./roas/v6-97.json?url";
import __glob_429 from "./roas/v6-98.json?url";
